import { zodResolver } from "@hookform/resolvers/zod"
import { addDays, differenceInDays } from "date-fns"
import { uniq } from "~/utils/tsUtils"

import { useCallback, useEffect, useMemo, useState } from "react"
import { useForm, useWatch } from "react-hook-form"
import { z } from "zod"

import { Button } from "~/components/reusable/Button"
import DatePicker from "~/components/reusable/DatePicker"
import InfoTooltip from "~/components/reusable/InfoTooltip"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "~/components/reusable/DropdownMenu"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/components/reusable/Form"
import { Input } from "~/components/reusable/Input"
import Select, { UserSelect, type ReactSelectOption } from "~/components/reusable/Select"
import { Module, WorkspacePage } from "~/components/design-system/pages"
import {
  useCreatePurchaseOrder,
  useSuggestedPurchaseOrder,
  useUpdatePurchaseOrder,
} from "~/hooks/usePurchaseOrders"
import { trackEvent } from "~/hooks/useTrackingService"
import { usePrimaryWarehouses } from "~/hooks/useWarehouses"
import { useUser } from "~/providers/userProvider"
import { useGetUsers } from "~/hooks/useUsers"
import { api } from "~/utils/api"
import { startOfTodayUTC } from "~/utils/datetime"

import { Label } from "../reusable/Label"
import LoadingOverlay from "../reusable/LoadingOverlay"
import { StatusBadge } from "../reusable/StatusBadge"
import { poStatusToColor, poStatusLabel } from "~/utils/purchaseOrders"
import { BreadcrumbWrapper } from "../reusable/Breadcrumb"
import { useBlocker, useNavigate } from "@tanstack/react-router"
import { useDownloadMultiplePurchaseOrders } from "~/hooks/useDownloads"
import {
  ArrowSquareOut,
  ArrowsClockwise,
  DownloadSimple,
  Factory,
  Warehouse,
} from "@phosphor-icons/react"
import PushBackDialog, {
  type PushBackDialogValues,
  type PushBackGroup,
  type PushBackSubmission,
} from "../planning/PushBackDialog"
import { ConfirmationDialog } from "../reusable/ConfirmationDialog"
import { toast } from "react-toastify"
import { usePermission } from "~/hooks/usePermission"
import { PermissionTooltip } from "~/components/design-system/components"
import { PurchaseOrderTableV2 } from "~/components/purchaseOrder/PurchaseOrderTableV2"
import { type PurchaseOrderWithItems } from "~/models/purchaseOrder"
import AddVariantsToPurchaseOrderDialog from "~/components/purchaseOrder/dialogs/AddVariantsToPurchaseOrderDialog"
import { isDevelopment } from "~/utils/environment"
import { useRegisterSearchCommands } from "~/components/sidebar/search/useRegisterSearchCommands"
import type { PageCommand } from "~/components/sidebar/search/types"

export function roundToColliSize(quantity: number, colliSize: number) {
  return Math.ceil(quantity / Math.max(colliSize, 1)) * Math.max(colliSize, 1)
}

const SAVE_BEFORE_SEND_TO_ERP = "Save your changes before sending to ERP"

const getSendToErpBlockedReason = (isDirty: boolean, isSchemaLoading: boolean) => {
  if (isDirty) return SAVE_BEFORE_SEND_TO_ERP
  if (isSchemaLoading) return "Loading ERP fields"
  return undefined
}

// Stable fallback so consumers can safely use `addedDemand` in memo/query dependencies.
const NO_ADDED_DEMAND: string[] = []

const poSchema = z.object({
  name: z.string(),
  expectedArrivalDate: z.date(),
  purchasePeriodEndDate: z.date(),
  vendorId: z.string(),
  status: z.object({ label: z.string(), value: z.string() }),
  warehouseIds: z.array(z.string()).optional(),
  isDirtyFlag: z.boolean(), // used to trigger is dirty on the form, when items change and to trigger leave on confirm
  addedDemand: z.array(z.string()).optional(), // used to store added demand for advanced demand mapping
  collectionIds: z.array(z.string()).optional(),
  assigneeId: z.string().nullable().optional(),
})

type POSchema = z.infer<typeof poSchema>

type IItem = { skuId: string; warehouseId: string; quantity: number }

const CreateOrUpdatePurchaseOrder = ({
  purchaseOrder,
}: {
  purchaseOrder?: PurchaseOrderWithItems
}) => {
  const navigate = useNavigate()
  const { permissions } = useUser()
  const { allowed: canConfigureExport } = usePermission("administer")
  const [poListReturnUrl] = useState(
    () => sessionStorage.getItem("po-list-return-url") ?? "/procurement/purchase-orders/draft",
  )

  const updatePurchaseOrder = useUpdatePurchaseOrder()
  const createPurchaseOrder = useCreatePurchaseOrder()
  const downloadPurchaseOrders = useDownloadMultiplePurchaseOrders()

  const { data: company } = api.company.getCompany.useQuery()
  const { data: users } = useGetUsers(isDevelopment())

  const collectionIds = purchaseOrder?.collectionIds?.map((id) => Number(id))
  const warehouses = usePrimaryWarehouses()
  const { data: vendors } = api.vendor.getAll.useQuery()

  const utils = api.useUtils()

  const { data: hasPushbackIntegration } = api.purchaseOrder.hasPushbackIntegration.useQuery({
    companyId: company?.orgId ?? "",
  })
  const { data: schema, isLoading: isSchemaLoading } = api.purchaseOrder.getPushbackSchema.useQuery(
    { companyId: company?.orgId ?? "" },
    { enabled: !!company && hasPushbackIntegration },
  )

  const poPushBack = api.purchaseOrder.pushBack.useMutation({
    onSuccess: async () => {
      toast.success("Purchase orders were sent succesfully to ERP")
      await utils.purchaseOrder.getAll.invalidate()
      await utils.purchaseOrder.get.invalidate()
      trackEvent("Purchase Order(s) Sent To ERP")
    },
    onError: async (error) => {
      toast.error(error.message)
    },
  })

  const handlePoPushBack = useCallback(
    (values: PushBackDialogValues) => {
      if (!company || !purchaseOrder) return
      // send pushback with extra fields
      poPushBack.mutate({
        companyid: company.orgId,
        purchaseOrderIds: [purchaseOrder.id],
        ...values,
      })
    },
    [poPushBack, company, purchaseOrder],
  )

  const [isPushDialogOpen, setIsPushDialogOpen] = useState(false)

  const exportPurchaseOrder = (fileFormat: "csv" | "excel") => {
    if (!purchaseOrder) return
    trackEvent("Purchase Order Exported", {
      "Download Format": fileFormat === "csv" ? "CSV" : "Excel",
    })
    downloadPurchaseOrders.mutate({ purchaseOrderIds: [purchaseOrder.id], fileFormat })
  }

  const getDefaultValues = useCallback(
    () => ({
      name: purchaseOrder?.name,
      expectedArrivalDate: purchaseOrder?.expectedArrivalDate,
      purchasePeriodEndDate: purchaseOrder?.purchasePeriodEndDate,
      status: purchaseOrder
        ? { label: poStatusLabel(purchaseOrder?.status), value: purchaseOrder?.status }
        : { label: "Draft", value: "draft" },
      warehouseIds: uniq(
        purchaseOrder?.items
          .map((item) => item.warehouseId)
          .filter((id) => !purchaseOrder?.addedDemandWarehouseIds.includes(id)),
      ),
      vendorId: purchaseOrder?.vendorId,
      addedDemand: purchaseOrder?.addedDemandWarehouseIds ?? [],
      isDirtyFlag: false,
      assigneeId: purchaseOrder?.assigneeId ?? null,
    }),
    [purchaseOrder],
  )

  const form = useForm<POSchema>({
    resolver: zodResolver(poSchema),
    defaultValues: getDefaultValues(),
    reValidateMode: "onSubmit",
  })

  // Prevent navigation when form is dirty, but skip blocker after a successful submit
  const { isDirty, isSubmitSuccessful } = form.formState
  useBlocker({
    shouldBlockFn: () => {
      // don't block if no unsaved changes or just saved successfully
      if (!isDirty || isSubmitSuccessful) return false
      const shouldLeave = confirm("Are you sure you want to leave without saving?")
      return !shouldLeave
    },
  })

  const warehouseIds = useWatch({ name: "warehouseIds", control: form.control })
  const vendorId = useWatch({ name: "vendorId", control: form.control })
  // Initialize items directly from purchaseOrder
  const [items, setItems_] = useState<IItem[]>(purchaseOrder?.items ?? [])

  const skuIds = useMemo(() => uniq(items.map((i) => i.skuId) ?? []), [items])

  useEffect(() => {
    if (purchaseOrder) {
      // Schedule the state update for the next render
      requestAnimationFrame(() => {
        setItems_(purchaseOrder.items)
      })
    }
    form.reset(getDefaultValues())
  }, [purchaseOrder, getDefaultValues, form])

  const setItems = (items_: IItem[]) => {
    const originalItems = purchaseOrder?.items ?? []
    const originalQuantities = new Map(
      originalItems.map((o) => [`${o.skuId}::${o.warehouseId}`, o.quantity]),
    )
    const matchesOriginal =
      items_.length === originalItems.length &&
      items_.every(
        (item) => originalQuantities.get(`${item.skuId}::${item.warehouseId}`) === item.quantity,
      )

    form.setValue("isDirtyFlag", !matchesOriginal, { shouldDirty: true })
    setItems_(items_)
  }
  const expectedArrivalDate = useWatch({ name: "expectedArrivalDate", control: form.control })
  const purchasePeriodEndDate = useWatch({ name: "purchasePeriodEndDate", control: form.control })

  const suggestedItems = useSuggestedPurchaseOrder(
    skuIds,
    warehouseIds?.length
      ? [...warehouseIds, ...(purchaseOrder?.addedDemandWarehouseIds ?? [])]
      : warehouses.map((w) => w.id),
    expectedArrivalDate ?? startOfTodayUTC(),
    purchasePeriodEndDate ?? startOfTodayUTC(),
  )

  // TODO: Investigate if this can be done more efficiently..
  useEffect(() => {
    if (warehouses?.length === 1) {
      form.setValue("warehouseIds", warehouses[0]?.id ? [warehouses[0]?.id] : [])
    }
  }, [warehouses, form])

  // Until these load the page shows a loading overlay, so a command would open a dialog later
  const isPageReady = !!purchaseOrder && !!warehouses && !!vendors
  const canSendToErp = isPageReady && !!hasPushbackIntegration
  const sendToErpBlockedReason = getSendToErpBlockedReason(isDirty, isSchemaLoading)
  const sendToErpCommands: PageCommand[] = canSendToErp
    ? [
        {
          id: "send-purchase-order-to-erp",
          name: "Send to ERP",
          keywords: ["push", "sync", "purchase order"],
          icon: <ArrowsClockwise />,
          requires: "edit",
          disabledReason: sendToErpBlockedReason,
          run: () => setIsPushDialogOpen(true),
        },
      ]
    : []
  useRegisterSearchCommands(
    isPageReady
      ? [
          ...(["csv", "excel"] as const).map(
            (fileFormat): PageCommand => ({
              id: `export-purchase-order-${fileFormat}`,
              name: `Export to ${fileFormat === "csv" ? "CSV" : "Excel"}`,
              keywords: ["download", "purchase order"],
              icon: <DownloadSimple />,
              run: () => exportPurchaseOrder(fileFormat),
            }),
          ),
          ...sendToErpCommands,
        ]
      : [],
    [purchaseOrder?.id, isPageReady, canSendToErp, sendToErpBlockedReason],
  )

  // TODO: Fix loading. isLoading is always true for some reason
  if (!warehouses || !vendors) {
    return <LoadingOverlay />
  }

  const onSubmit = async (values: POSchema) => {
    // Enter-key form submission bypasses the disabled submit button.
    if (!permissions.canEdit) return

    if (purchaseOrder) {
      updatePurchaseOrder.mutate(
        {
          purchaseOrderId: purchaseOrder.id,
          name: values.name,
          expectedArrivalDate,
          purchasePeriodEndDate,
          items: items,
          status: values.status.value,
          vendorId: values.vendorId,
          collectionIds: values.collectionIds,
          assigneeId: values.assigneeId ?? null,
        },
        {
          onSuccess: () => {
            trackEvent("Purchase Order Edited", {
              "Purchase Order Edit Source": "Purchase Order",
            })
            // clear form dirty state
            form.reset(getDefaultValues())
          },
        },
      )
    } else {
      // TODO: Consider adding loading screen
      trackEvent("Purchase Order Created")
      createPurchaseOrder.mutate(
        {
          name: values.name,
          expectedArrivalDate,
          purchasePeriodEndDate,
          items: items,
          vendorId: values.vendorId,
          collectionIds: values.collectionIds ?? [],
        },
        {
          onSuccess: (data) =>
            navigate({
              to: "/procurement/purchase-orders/update/$purchaseOrderId",
              params: { purchaseOrderId: String(data.id) },
            }),
        },
      )
    }
  }

  const poStatusOptions: ReactSelectOption<string>[] = [
    { label: "Draft", value: "draft" },
    { label: "Draft - Approved", value: "draft_approved" },
    { label: "Sent", value: "sent" },
    { label: "Delivered", value: "delivered" },
    { label: "Registered", value: "registered" },
  ]

  const disabledPoStatusOptions: string[] = ["registered"]

  const addSkus = (skus: IItem[], expectedDate: Date, periodEnd: Date) => {
    if (expectedArrivalDate === undefined) {
      form.setValue("expectedArrivalDate", expectedDate)
    }

    if (purchasePeriodEndDate === undefined) {
      form.setValue("purchasePeriodEndDate", periodEnd)
    }

    setItems([...items, ...skus])
  }

  const breadcrumb = (
    <>
      <BreadcrumbWrapper
        items={[
          { label: "Procurement", href: "/procurement" },
          { label: "Purchase Orders", href: poListReturnUrl },
          {
            label: purchaseOrder ? purchaseOrder?.name : "Create new purchase order",
          },
        ]}
      />
      {purchaseOrder && (
        <StatusBadge
          color={poStatusToColor(purchaseOrder.status)}
          text={poStatusLabel(purchaseOrder.status)}
        />
      )}
    </>
  )

  const actions = (
    <>
      <AddVariantsToPurchaseOrderDialog
        alreadySelectedSkus={items.map((i) => i.skuId)}
        addSkus={addSkus}
        selectedVendor={vendorId}
        selectedWarehouse={warehouseIds ? warehouseIds[0] : undefined}
        addedDemand={purchaseOrder?.addedDemandWarehouseIds ?? NO_ADDED_DEMAND}
        startDate={expectedArrivalDate}
        endDate={purchasePeriodEndDate}
      />
      <DropdownMenu>
        <DropdownMenuTrigger>
          <Button variant="neutral" asChild size="sm" disabled={purchaseOrder === undefined}>
            <DownloadSimple />
            Export
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuItem
            onSelect={(e) => {
              e.preventDefault()
              exportPurchaseOrder("csv")
            }}
          >
            CSV
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={(e) => {
              e.preventDefault()
              exportPurchaseOrder("excel")
            }}
          >
            Excel
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <PermissionTooltip requires="administer" side="right" wrapperClassName="w-full align-top">
            <DropdownMenuItem
              className="w-full"
              disabled={!canConfigureExport}
              onSelect={() => window.open("/settings/procurement", "_blank")}
            >
              <ArrowSquareOut />
              Configure export
            </DropdownMenuItem>
          </PermissionTooltip>
        </DropdownMenuContent>
      </DropdownMenu>
      <Button
        requires="edit"
        size="sm"
        disabled={updatePurchaseOrder.isPending || (!!purchaseOrder && !isDirty)}
        loading={updatePurchaseOrder.isPending}
        type="submit"
      >
        {purchaseOrder ? "Update" : "Create"}
      </Button>
      {hasPushbackIntegration &&
        purchaseOrder &&
        (schema && schema.length > 0 ? (
          <PushBackDialog
            trigger={
              <Button variant="neutral" size="sm" type="button">
                <ArrowsClockwise />
                Send to ERP
              </Button>
            }
            requires="edit"
            allowedTitle={isDirty ? SAVE_BEFORE_SEND_TO_ERP : undefined}
            disabled={isDirty || isSchemaLoading || !(schema && schema.length > 0)}
            open={isPushDialogOpen}
            onOpenChange={setIsPushDialogOpen}
            onConfirm={(submissions: PushBackSubmission[]) => {
              trackEvent("Purchase Order(s) Sent To ERP")
              const first = submissions[0]
              if (first) handlePoPushBack(first.values)
            }}
            schema={schema ?? []}
            groups={
              [
                {
                  id: String(purchaseOrder.id),
                  label: purchaseOrder.name,
                  purchaseOrderIds: [purchaseOrder.id],
                },
              ] satisfies PushBackGroup[]
            }
          />
        ) : (
          <ConfirmationDialog
            title="Send purchase orders"
            description="Are you sure you want to send the selected purchase orders to your ERP system?"
            open={isPushDialogOpen}
            onOpenChange={setIsPushDialogOpen}
            onConfirm={() => {
              handlePoPushBack({})
              setIsPushDialogOpen(false)
            }}
            trigger={
              <Button variant="neutral" size="sm" type="button">
                <ArrowsClockwise />
                Send to ERP
              </Button>
            }
            requires="edit"
            disabled={isDirty}
            disabledTooltip={SAVE_BEFORE_SEND_TO_ERP}
          />
        ))}
    </>
  )

  const toolbar = (
    <div className="flex flex-row flex-wrap gap-x-4 gap-y-2 items-end w-full">
      <Input label="ID" value={purchaseOrder?.id ?? "-"} disabled containerClassName="w-[64px]" />
      <FormField
        control={form.control}
        name="name"
        render={({ field }) => (
          <FormItem>
            <FormControl>
              <Input label="Purchase order name" containerClassName="w-[220px]" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="warehouseIds"
        render={({ field }) => (
          <FormItem>
            <FormControl>
              <Select
                label="Warehouse"
                className="min-w-[240px] w-fit"
                onChange={(value) => value && field.onChange([value].map((x) => x.id))} // TODO: Remove [] when we support multiple warehouses
                value={warehouses?.filter((w) => warehouseIds?.includes(w.id))}
                options={(warehouses ?? []).filter((w) => w.isPrimary)}
                getOptionLabel={(option) => option.name}
                getOptionValue={(option) => option.id}
                isDisabled={warehouseIds !== undefined && items?.length > 0}
                // isMulti
                isClearable={false}
                leftIcon={<Warehouse />}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      {purchaseOrder?.addedDemandWarehouseIds &&
        purchaseOrder?.addedDemandWarehouseIds.length > 0 && (
          <FormField
            control={form.control}
            name="addedDemand"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Select
                    label="Added demand"
                    className="min-w-[125px]"
                    {...field}
                    value={
                      warehouses
                        ?.filter((w) => field.value?.includes(w.id))
                        .map((w) => ({ label: w.name, value: w.id, isFixed: true })) ?? []
                    }
                    isMulti
                    placeholder="none"
                    isDisabled
                  />
                </FormControl>
              </FormItem>
            )}
          />
        )}
      <FormField
        control={form.control}
        name="vendorId"
        render={({ field }) => (
          <FormItem>
            <FormControl>
              <Select
                label="Vendor"
                className="min-w-[180px] max-w-[180px]"
                onChange={(value) => value && field.onChange(value.vendorId)}
                value={vendors?.find((v) => v.vendorId === vendorId)}
                options={vendors ?? []}
                getOptionLabel={(option) => option.name}
                getOptionValue={(option) => option.vendorId}
                isDisabled={vendorId !== undefined && items?.length > 0}
                leftIcon={<Factory />}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="expectedArrivalDate"
        render={({ field }) => (
          <FormItem>
            <div className="flex items-center gap-1">
              <FormLabel>Expected arrival date</FormLabel>
              <InfoTooltip title="Changing this date will recalculate the suggested quantities for this purchase order." />
            </div>
            <FormControl>
              <DatePicker
                value={expectedArrivalDate}
                onChange={(date) => {
                  if (date) {
                    field.onChange(date)
                    if (expectedArrivalDate !== undefined && purchasePeriodEndDate) {
                      const difference = differenceInDays(
                        purchasePeriodEndDate,
                        expectedArrivalDate,
                      )
                      if (difference < 0) {
                        form.setValue("purchasePeriodEndDate", addDays(date, 1))
                      } else {
                        form.setValue("purchasePeriodEndDate", addDays(date, difference))
                      }
                    }
                  }
                }}
                minDate={startOfTodayUTC()}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="purchasePeriodEndDate"
        render={({ field }) => (
          <FormItem>
            <div className="flex flex-row items-center gap-1">
              <FormLabel>End of purchase period</FormLabel>
              <InfoTooltip title="Length of the period you want your purchase order to cover." />
            </div>
            <FormControl>
              <DatePicker
                value={purchasePeriodEndDate}
                onChange={(date) => date && field.onChange(date)}
                minDate={addDays(expectedArrivalDate, 1)}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <div>
        <Label>Status</Label>
        <FormField
          control={form.control}
          name="status"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Select
                  className="min-w-[125px]"
                  {...field}
                  options={poStatusOptions}
                  defaultValue={{ label: "Draft", value: "draft" }}
                  isOptionDisabled={(option) =>
                    disabledPoStatusOptions.includes(option.value) &&
                    !disabledPoStatusOptions.includes(purchaseOrder?.status ?? "")
                  }
                />
              </FormControl>
            </FormItem>
          )}
        />
      </div>
      <FormField
        control={form.control}
        name="assigneeId"
        render={({ field }) => (
          <FormItem>
            <FormControl>
              <UserSelect
                label="Assignee"
                users={users ?? []}
                selectedUser={users?.find((u) => u.id === field.value) ?? undefined}
                onChange={(u) => {
                  const user = users?.find((usr) => usr.email === u?.value)
                  field.onChange(user?.id ?? null)
                }}
                isClearable
                placeholder="Select assignee"
              />
            </FormControl>
          </FormItem>
        )}
      />
    </div>
  )

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="contents">
        <Module breadcrumb={breadcrumb} actions={actions}>
          <WorkspacePage toolbar={toolbar} fillContent>
            {purchasePeriodEndDate && expectedArrivalDate && (
              <PurchaseOrderTableV2
                items={items}
                setItems={setItems}
                suggestedItems={suggestedItems ?? []}
                nextOrderArrivalDate={purchasePeriodEndDate}
                expectedArrivalDate={expectedArrivalDate}
                addedDemand={form.getValues("addedDemand") ?? NO_ADDED_DEMAND}
                collectionIds={collectionIds}
                purchaseOrderId={purchaseOrder?.id}
                vendorId={vendorId}
                purchasePlanId={purchaseOrder?.purchasePlanId ?? undefined}
                poStatus={purchaseOrder?.status}
              />
            )}
          </WorkspacePage>
        </Module>
      </form>
    </Form>
  )
}

export default CreateOrUpdatePurchaseOrder
